import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Sun, ArrowLeft, Mail, Lock, Sparkles, ShieldCheck, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('sales@inpacepowers.com');
  const [password, setPassword] = useState('inpace@2025');
  const [loading, setLoading] = useState(false);

  // Read ?role=admin (or sales) to pre-fill credentials
  useEffect(() => {
    const role = params.get('role');
    if (role === 'admin' || role === 'director') {
      setEmail('director@inpacepowers.com');
      setPassword('inpace@2025');
    } else if (role === 'sales') {
      setEmail('sales@inpacepowers.com');
      setPassword('inpace@2025');
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Trim whitespace and lowercase email — copy/paste from WhatsApp/Notes often adds spaces
    const cleanEmail = (email || '').trim().toLowerCase();
    const cleanPassword = (password || '').trim();
    const res = await login(cleanEmail, cleanPassword);
    setLoading(false);
    if (res.ok) {
      toast.success('Welcome back!');
      const next = params.get('next');
      nav(next && next.startsWith('/app') ? next : '/app');
    } else {
      toast.error(res.msg);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex relative bg-gradient-to-br from-emerald-700 to-emerald-950 p-12 flex-col justify-between text-white overflow-hidden">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative">
          <Logo variant="light" />
        </div>
        <div className="relative">
          <h1 className="text-4xl font-extrabold leading-tight">Close more solar deals <br />on the very first visit.</h1>
          <p className="mt-4 text-emerald-100/80 max-w-md">Generate a polished proposal with shading analysis, BOM and financials — in 20 minutes, right at the customer’s site.</p>
          <div className="mt-10 space-y-4">
            {[
              { icon: Zap, t: 'Instant savings calculator', d: 'Per-segment tariffs & subsidies built-in.' },
              { icon: Sparkles, t: 'AI-style design suite', d: 'Drop panels on the rooftop and see generation update live.' },
              { icon: ShieldCheck, t: 'Branded PDF on the spot', d: 'Email or WhatsApp the proposal before you leave.' },
            ].map(({ icon: I, t, d }) => (
              <div key={t} className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0"><I className="w-5 h-5 text-amber-300" /></div>
                <div>
                  <div className="font-semibold">{t}</div>
                  <div className="text-sm text-emerald-100/70">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-emerald-200/60">© {new Date().getFullYear()} Inpace Power. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 lg:p-12 bg-emerald-50/30">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-emerald-700 mb-6"><ArrowLeft className="w-4 h-4 mr-1" /> Back to website</Link>
          <Card className="p-8 border-emerald-100 shadow-xl shadow-emerald-100/40">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-5 h-5 text-amber-500" />
              <span className="text-xs font-bold tracking-widest text-emerald-700 uppercase">Sales Portal</span>
            </div>
            <h2 className="text-3xl font-extrabold text-emerald-950">Sign in to your account</h2>
            <p className="mt-2 text-sm text-slate-500">Use your Inpace Power sales credentials.</p>

            {/* Role quick-swap */}
            <div className="mt-5 flex gap-2 flex-wrap">
              <button
                type="button"
                data-testid="quick-sales-btn"
                onClick={() => { setEmail('sales@inpacepowers.com'); setPassword('inpace@2025'); }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${
                  email === 'sales@inpacepowers.com'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                    : 'border-emerald-100 text-slate-600 hover:border-emerald-300'
                }`}
              >
                Sales Executive
              </button>
              <button
                type="button"
                data-testid="quick-admin-btn"
                onClick={() => { setEmail('director@inpacepowers.com'); setPassword('inpace@2025'); }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${
                  email === 'director@inpacepowers.com'
                    ? 'border-amber-500 bg-amber-50 text-amber-800'
                    : 'border-emerald-100 text-slate-600 hover:border-emerald-300'
                }`}
              >
                Admin / Director
              </button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-slate-700">Work email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={email} onChange={e => setEmail(e.target.value)} type="email" className="pl-10 h-11" placeholder="you@inpacepowers.com" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-slate-700">Password</Label>
                  <a className="text-xs text-emerald-700 hover:underline">Forgot?</a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={password} onChange={e => setPassword(e.target.value)} type="password" className="pl-10 h-11" />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in to Sales Tool'}
              </Button>
              <p className="text-center text-xs text-slate-500">Tap a role above to auto-fill credentials · Password: <code className="text-emerald-800">inpace@2025</code></p>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
